<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-horizontal="start"
            align-vertical="end"
        >
            <DCol sm="6">
                <DButton
                    id="filter-in-progress"
                    :label="__('orders.in_progress')"
                    @click="quickFilter"
                />
            </DCol>
            <DCol
                sm="6"
                class="text-right"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <DRow
            class="mb-3"
            align-horizontal="start"
            align-vertical="end"
        >
            <DCol sm="3">
                <DInput
                    id="search"
                    v-model="filterData.search"
                    :label="__('orders.order_number')"
                    :placeholder="__('orders.search')"
                    @keydown.enter.exact.prevent="search"
                />
            </DCol>
            <DCol sm="2">
                <DInput
                    id="search"
                    v-model="filterData.customer"
                    :label="__('orders.customer')"
                    :placeholder="__('orders.search')"
                    @keydown.enter.exact.prevent="search"
                />
            </DCol>
            <DCol sm="2">
                <DMultiSelect
                    id="status-dropdown"
                    :label="__('orders.status')"
                    :options="statusOptions"
                    selection-type="text"
                    :selected="filterData.statuses"
                    @update="updateSelectedStatuses"
                />
            </DCol>
            <DCol sm="2">
                <DMultiSelect
                    id="contract-manager-dropdown"
                    :label="__('orders.check.contract_manager')"
                    :options="contractManagerOptions"
                    selection-type="text"
                    :selected="filterData.contractManagers"
                    @update="updateSelectedContractManagers"
                />
            </DCol>
            <DCol
                sm="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="search"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DRow>
            <DCol sm="12">
                <DCard
                    class="orders-box"
                >
                    <template #default>
                        <DTable
                            :fields="getFields"
                            :items="orderCollection.orders.concat(invalidOrders)"
                            class="table-responsive"
                            :sorter="isSortable"
                            :sorter-value="sorterValue"
                            @update:sorter-value="updateOrdering"
                        >
                            <template #orderNumber="{item}">
                                <td>{{ item.orderNumber }} </td>
                                <td
                                    v-if="!item.valid"
                                    :colspan="colspan"
                                >
                                    <DTag
                                        :label="__('orders.invalid_order')"
                                        color="danger"
                                    /> {{ item.error }}
                                </td>
                            </template>
                            <template #payment="{item}">
                                <td v-if="item.valid">
                                    {{ item.totalAmount }}<br>
                                    <DTag
                                        v-if="item.customer"
                                        :label="'via ' + item.customer.payment_method"
                                    />
                                </td>
                            </template>
                            <template #entryDate="{item}">
                                <td v-if="item.valid && item.entryDate">
                                    {{ setDateFormat(item.entryDate) }}
                                </td>
                                <td v-if="item.valid && !item.entryDate" />
                            </template>
                            <template #status="{item}">
                                <td v-if="item.valid && item.status">
                                    <DTag :label="__('orders.status_'+item.status)" />
                                </td>
                                <td v-if="item.valid && !item.status" />
                            </template>
                            <template #customer="{item}">
                                <td
                                    v-if="item.valid && item.customer"
                                >
                                    <div v-if="item.customer.company">
                                        {{ item.customer.company }}
                                    </div>
                                    <div v-if="item.customer.firstname || item.customer.lastname">
                                        <span v-if="item.customer.firstname">{{ item.customer.firstname }}</span>
                                        <span v-if="item.customer.lastname">{{ item.customer.lastname }}</span>
                                    </div>
                                    <Link
                                        v-if="item.clientCustomerId !== null"
                                        :href="$route('customers.customers.show', {customer: item.clientCustomerId})"
                                    >
                                        {{ item.clientCustomerId }}
                                    </Link>
                                </td>
                                <td v-if="item.valid && !item.customer" />
                            </template>
                            <template #products="{item}">
                                <td v-if="item.valid">
                                    <p
                                        v-for="(product, productIndex) in item.products"
                                        :key="productIndex"
                                    >
                                        - <strong>{{ product.domainName !== null ? product.domainName : product.name }}</strong>
                                    </p>
                                    <p v-if="item.transferId !== undefined && item.transferId !== null">
                                        <Link
                                            :href="$route('contracts.transfers', {transferId: item.transferId})"
                                        >
                                            - Contract transfer:  {{ item.transferId }}
                                        </Link>
                                    </p>
                                </td>
                            </template>
                            <template #targetInvoiceDate="{item}">
                                <td v-if="item.valid">
                                    <span v-if="item.payment.invoice_target_date">
                                        {{ setDateFormat(item.payment.invoice_target_date) }}
                                    </span>
                                </td>
                            </template>
                            <template #salesAgent="{item}">
                                <td v-if="item.valid">
                                    <span v-if="item.salesAgent">
                                        {{ item.salesAgent }}
                                    </span>
                                </td>
                            </template>
                            <template #contractManager="{item}">
                                <td v-if="item.valid">
                                    <span v-if="item.contractManager">
                                        {{ item.contractManager }}
                                    </span>
                                </td>
                            </template>
                            <template #priority="{item}">
                                <td v-if="item.valid">
                                    <span v-if="item.priority">
                                        {{ item.priority }}
                                    </span>
                                </td>
                            </template>
                            <template #actions="{item}">
                                <td style="display: flex; grid-gap: 5px;">
                                    <DButton
                                        mini
                                        icon="eye"
                                        @click="orderDetail(item)"
                                    />
                                    <DButton
                                        v-if="userPermissions.includes('order.view')"
                                        mini
                                        icon="cloud-check-variant-outline"
                                        @click="checkOrder(item)"
                                    />
                                </td>
                            </template>
                        </DTable>
                        <DPagination
                            :active-page="orderCollection.page"
                            :pages="orderCollection.totalPages"
                            align="center"
                            @update:activePage="loadPage"
                        />
                        <OrderNoteCreateModal
                            v-if="showNoteCreateModal"
                            :show-modal="showNoteCreateModal"
                            :title="`${__('orders.note.title')} ${selectedItem.orderNumber}`"
                            :order-note="orderNoteDialog.currentNote"
                            :errors="$page.props.errors.orderNoteDialog"
                            @close="showNoteCreateModal = false"
                            @confirm="saveOrderNote"
                        />
                        <DModal
                            :show="showNoteModal"
                            :title="`${__('orders.note.title')} ${selectedItem.orderNumber}`"
                            is-big
                            @close="showNoteModal = false"
                        >
                            <div class="p-3">
                                <DFormTextarea
                                    :value="selectedItem.orderNote ? selectedItem.orderNote.note : ''"
                                    :disabled="true"
                                    rows="5"
                                />
                            </div>
                        </DModal>
                    </template>
                </DCard>
            </DCol>
        </DRow>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script>
import OrderNoteCreateModal from '~@/Components/Orders/OrderNoteCreateModal'
import ListViewModal from '~@/Components/ListView/ListViewModal'
import { customerBankDetails } from '~@/Composables/sepaMandate'

export default {
    name: 'PsaOrderIndex',
    components: {
        ListViewModal,
        OrderNoteCreateModal
    },
    props: {
        orderCollection: {
            type: Object,
            default () {
                return {}
            }
        },
        invalidOrders: {
            type: Array,
            default () {
                return []
            }
        },
        errors: {
            type: Object,
            default () {
                return {}
            }
        },
        page: {
            type: Number,
            default: 1
        },
        filters: {
            type: Object,
            default () {
                return {}
            }
        },
        contractManagers: {
            type: Array,
            default () {
                return []
            }
        },
        orderClient: {
            type: String,
            default: ''
        },
        orderBy: {
            type: String,
            default: 'id'
        },
        orderByAsc: {
            type: Boolean,
            default: false
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        }
    },
    data () {
        return {
            showRejectDialog: false,
            dialogTitle: '',
            dialogMessage: '',
            databaseCustomers: [],
            selectedItem: {},
            orderNoteDialog: {
                currentNote: {}
            },
            showNoteModal: false,
            showAcceptDialog: false,
            showNoteCreateModal: false,
            filterData: {
                statuses: this.filters.statuses,
                search: this.filters.search,
                customer: this.filters.customer,
                contractManagers: this.filters.contractManagers
            },
            orderByData: this.orderBy,
            orderByAscData: this.orderByAsc ? 0 : 1,
            showListViewModal: false,
            colspan: 8
        }
    },
    computed: {
        statusOptions () {
            return [
                { value: 'open', text: this.__('orders.status_open'), selected: this.filterData.statuses.includes('open') },
                { value: 'screening', text: this.__('orders.status_screening'), selected: this.filterData.statuses.includes('screening') },
                { value: 'clarification_external', text: this.__('orders.status_clarification_external'), selected: this.filterData.statuses.includes('clarification_external') },
                { value: 'clarification_internal', text: this.__('orders.status_clarification_internal'), selected: this.filterData.statuses.includes('clarification_internal') },
                { value: '4_eyes_check', text: this.__('orders.status_4_eyes_check'), selected: this.filterData.statuses.includes('4_eyes_check') },
                { value: 'accepted', text: this.__('orders.status_accepted'), selected: this.filterData.statuses.includes('accepted') },
                { value: 'rejected', text: this.__('orders.status_rejected'), selected: this.filterData.statuses.includes('rejected') }
            ]
        },
        contractManagerOptions () {
            return this.contractManagers.map((item) => {
                return { value: item.value, text: item.label, selected: this.filterData?.contractManagers?.includes(String(item.value)) }
            })
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        getFields () {
            const availableList = []

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    availableList.push({
                        key: item.column,
                        label: this.__('settings.list_view.' + item.list + '.' + item.column),
                        sorter: item.sort_column_enabled,
                        _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
                    })
                }
            })

            this.getColSpan()

            return availableList
        },
        sorterValue () {
            return {
                column: this.orderByData,
                asc: !!this.orderByAscData
            }
        },
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        }
    },
    created () {
        this.getColSpan()
    },
    methods: {
        loadPage (page) {
            this.$inertia.get(this.$route('orders.psa.index', { _query: this.filterData, page: page, orderBy: this.orderByData, orderByAsc: this.orderByAscData ? 0 : 1 }))
        },
        quickFilter () {
            this.filterData.search = null
            this.filterData.customer = null
            this.filterData.contractManagers = []
            this.filterData.statuses = [
                'screening',
                'clarification_external',
                'clarification_internal',
                '4_eyes_check'
            ]

            this.$inertia.get(this.$route('orders.psa.index', { _query: this.filterData, page: this.page, orderBy: this.orderByData, orderByAsc: this.orderByAscData ? 0 : 1 }))
        },
        showOrderNoteDialog (order) {
            this.selectedItem = order
            this.showNoteCreateModal = true

            this.orderNoteDialog.currentNote = order.orderNote ?? {
                id: null, // new note
                order_id: order.id,
                oder_no: order.orderNumber,
                note: ''
            }
        },
        saveOrderNote (item) {
            // This seems to be overly complex but is the result of not being able
            // to trigger a normal form submit when a form is within a modal...
            // as the confirm button of the modal is outside the form
            if (item.id !== null) {
                this.$inertia.patch(
                    this.$route('ordernotes.update', {
                        orderClient: this.orderClient,
                        orderNote: item.id,
                        _query: { page: this.page, filter: this.filterData.statuses }
                    }),
                    item,
                    {
                        errorBag: 'orderNoteDialog',
                        onFinish: () => {
                            // Open again if there is any error
                            this.showNoteCreateModal = !!this.errors.orderNoteDialog
                        }
                    }
                )
            } else {
                this.$inertia.post(this.$route('ordernotes.create', {
                    orderClient: this.orderClient,
                    orderId: this.selectedItem.id,
                    orderNumber: this.selectedItem.orderNumber,
                    _query: { page: this.page, filter: this.filterData.statuses }
                }), item, {
                    errorBag: 'orderNoteDialog',
                    onFinish: () => {
                        // Open again if there is any error
                        this.showNoteCreateModal = !!this.errors.orderNoteDialog
                    }
                })
            }
        },
        search () {
            this.filterData.search = this.filterData.search !== null ? this.filterData.search.trim() : null
            this.$inertia.get(this.$route('orders.psa.index', { _query: this.filterData, page: this.page, orderBy: this.orderByData, orderByAsc: this.orderByAscData ? 0 : 1 }))
        },
        itemAcceptable (item) {
            const acceptableStatuses = ['open', 'rejected']

            if (acceptableStatuses.includes(item.status) && item.valid) {
                return true
            }

            if (!item.valid && item.hasInvalidPaymentData) {
                return true
            }
        },
        orderDetail (item) {
            this.$inertia.get(this.$route('orders.details', { order: item.orderData.id, client: 'PSA' }))
        },
        checkOrder (item) {
            this.$inertia.get(
                this.$route('orders.psa.automatic-checks', {
                    order: item.orderNumber,
                    client: 'PSA'
                })
            )
        },
        getCustomerBankDetails (customer) {
            return customerBankDetails(customer)
        },
        resetFilters () {
            this.$inertia.get(this.$route('orders.psa.index', { _query: { page: this.page } }))
        },
        showNote (order) {
            this.selectedItem = order
            this.showNoteModal = true
        },
        updateSelectedStatuses (values) {
            this.filterData.statuses = values
        },
        updateSelectedContractManagers (values) {
            this.filterData.contractManagers = values
        },
        updateOrdering (event) {
            this.$inertia.get(this.$route('orders.psa.index', { _query: this.filterData, page: this.page, orderBy: event.column, orderByAsc: event.asc ? 0 : 1 }))
        },
        getColSpan () {
            let activeListLength = 0

            this.userListView.forEach((item) => {
                if (item.show_column === true) {
                    ++activeListLength
                }
            })

            const colspan = activeListLength - 3

            if (colspan < 0) {
                this.colspan = 0

                return
            }

            if (colspan < 3) {
                this.colspan = colspan + 1

                return
            }

            this.colspan = colspan
        }
    }
}
</script>

<style lang="scss">
// Forcing Status Column to be bigger despite having no space.
// Doing it manually because CoreUI can't scale a Column if the space is too tight
.orders-box th:nth-child(3) {
    width: 170px;
}
.database-customers {
    align-items: flex-end;
    gap: 10px;
}
.select-margin {
    margin-bottom: -8px;
}
.select-customer-table th:nth-child(1) {
    width: 20% !important;
}
.select-customer-table th:nth-child(2) {
    width: 40% !important;
}
.select-customer-table th:nth-child(3) {
    width: unset !important;
    border-bottom-width: 1px;
}
.different-customer-data {
    background-color: #ffff66;
}
.select-customer-table th {
    border-bottom: none !important;
}
.orders-blocked-text {
    color: var(--colorDanger);
    font-weight: bold;
}
</style>
